/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";

// Soft UI Dashboard PRO React components

import { getData, postData, delData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

import DialogBox from "Modals/CheckLeavePage";
import { useCallbackPrompt } from "hooks/useCallbackPrompt";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000", //"#626262",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 600,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

let settlementOutcomes = [];
let availableProviders = [];
let allDataTableData = {};
let currentDataTableData = {};
let confirmationsList = [];

/**
 *
 * @returns
 */
function Confirmation() {
  const [controller, dispatch] = useSoftUIController();
  const { platform, permissions, sports, providers } = controller;

  const [allowCreate, setAllowCreate] = useState(false);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState("UNCONFIRMED");

  const [showDialog, setShowDialog] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      //settlement data
      { Header: "Odd UUID", accessor: "uuid", width: "100px", Cell: formatUuid },
      { Header: "Market Name", accessor: "name", width: "200px", Cell: renderCell },

      { Header: "Odd e", accessor: "e", width: "100px" },
      { Header: "Odd h", accessor: "h", width: "100px" },
      { Header: "Odd o", accessor: "o", width: "100px" },
      { Header: "Odd Name", accessor: "odd_name", width: "150px", Cell: renderCell },
      { Header: "Operator Outcome", accessor: "settlement", width: "120px", Cell: showUser },
      {
        Header: "Calculated Outcome",
        accessor: "calculated_settlement",
        width: "80px",
        Cell: showVotes,
      },
      { Header: "Confirmation", accessor: "confirmed", width: "100px", Cell: confirmationCell },
      //event data
      { Header: "Fixture", accessor: "fixture", width: "100px", Cell: fixtureCell },

      //bet data
      { Header: "Platform", accessor: "platforms", width: "100px", Cell: platformsCell },
    ],

    rows: [],
  });

  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [externalSearch, setExternalSearch] = useState(null);

  let navigate = useNavigate();
  let location = useLocation();

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  function openAdvancedSearch(e, data) {
    e.preventDefault();

    window.open("/settlement/advanced?" + data[0] + "=" + data[1]);
  }

  const informParent = (data) => {
    if (data.key == "search") {
      setExternalSearch(null);
      if (data.value) {
        let datatable = { ...allDataTableData };
        datatable.rows = datatable.rows.filter((r) => JSON.stringify(r).indexOf(data.value) > -1);
        setDataTableData(datatable);
        currentDataTableData = datatable;
      } else {
        let datatable = { ...allDataTableData };
        setDataTableData(datatable);
        currentDataTableData = datatable;
      }
    }
    if (data.key == "refresh") {
      handleCancelEdit();
    }
  };

  function formatMarketName({ value, ...rest }) {
    return <div className="cell-double-width">{value}</div>;
  }

  function renderCell({ value, ...rest }) {
    return <div className="cell-width">{value}</div>;
  }

  function fixtureCell({ value, ...rest }) {
    value = formatDate({ value: rest.row.original.start_date_utc, ...rest });
    let statuses = formatStatuses({ value: rest.row.original.statuses, ...rest });
    let selectedFixture = rest.row.original;

    return (
      <div style={{ display: "flex" }}>
        <div>
          <div style={{ wordWrap: "auto", width: "80px" }}>{value}</div>
          <div style={{ wordWrap: "auto", width: "80px" }}>{statuses}</div>
        </div>

        <div>
          <div style={{ display: "flex", paddingBottom: "6px" }}>
            <div>
              {rest.row.original.sport} <Icon>navigate_next_icon </Icon>
            </div>
            <div>
              {rest.row.original.region} <Icon>navigate_next_icon </Icon>
            </div>
            <div>{rest.row.original.competition}</div>
          </div>
          <div>
            <div>{rest.row.original.home}</div>
            <div>{rest.row.original.away}</div>
          </div>
        </div>

        <div style={{ display: "flex" }}>
          <div>
            <HtmlTooltip
              placement="right"
              title={
                <React.Fragment>
                  <div style={{}}>
                    <div>
                      {"UniID: "}
                      {rest.row.original.uni_id ? rest.row.original.uni_id : "-"}
                    </div>
                    {rest.row.original.providers.map((item, index) => {
                      let provider = availableProviders.find((p) => p.value === item);
                      let providerName = provider ? provider["info"] : item;
                      return (
                        <div
                          key={item + "-" + rest.row.original.fixtureIds[index]}
                          style={{ display: "flex" }}
                        >
                          <div className={"provider-" + providerName + "-inverse"}>
                            {providerName}
                          </div>
                          <div>
                            {": "} {rest.row.original.fixtureIds[index]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              }
            >
              <Button
                style={{
                  padding: 0,
                  padding: "0px",
                  width: "30px",
                  maxWidth: "30px",
                  minWidth: "10px",
                  marginTop: "80%",
                }}
              >
                <span style={{ paddingLeft: "5px" }}> </span>
                <Icon>info_outlined </Icon>
              </Button>
            </HtmlTooltip>
          </div>
          <div style={{ borderLeft: "1px solid #cecece" }}>
            <SoftBox style={{ marginLeft: "auto" }}>
              <Button
                className="betfoot-button"
                style={{
                  display: selectedFixture
                    ? selectedFixture.providers.findIndex((item) => item == 3) > -1
                      ? ""
                      : "none"
                    : "none",
                }}
                disabled={!selectedFixture}
                onClick={(e) =>
                  searchBetfoot(
                    e,
                    selectedFixture
                      ? selectedFixture.fixtureIds[
                          selectedFixture.providers.findIndex((item) => item == 3)
                        ]
                      : null,
                    selectedFixture ? selectedFixture.sport.replace(/ /g, "-").toLowerCase() : null
                  )
                }
              >
                1xbet Results
              </Button>
            </SoftBox>
            <SoftBox style={{ marginLeft: "auto" }}>
              <Button
                className="vbet-button"
                style={{
                  display: selectedFixture
                    ? selectedFixture.providers.findIndex((item) => item == 2) > -1
                      ? ""
                      : "none"
                    : "none",
                }}
                disabled={!selectedFixture}
                onClick={(e) =>
                  searchVbet(
                    e,
                    selectedFixture
                      ? selectedFixture.fixtureIds[
                          selectedFixture.providers.findIndex((item) => item == 2)
                        ]
                      : null
                  )
                }
              >
                Vbet Results
              </Button>
            </SoftBox>
            <SoftBox>
              <Button
                className="sofa-button"
                disabled={!selectedFixture}
                onClick={(e) => searchSofa(e, selectedFixture)}
              >
                Search SofaScore
              </Button>
            </SoftBox>
            <SoftBox>
              <Button
                disabled={!selectedFixture}
                onClick={(e) =>
                  searchGoogle(
                    e,
                    selectedFixture ? selectedFixture.home + " vs " + selectedFixture.away : null
                  )
                }
              >
                search web
              </Button>
            </SoftBox>
            <SoftBox>
              <Button
                color="warning"
                disabled={!selectedFixture}
                onClick={(e) =>
                  searchBetsApi(
                    e,
                    selectedFixture ? selectedFixture.home + " vs " + selectedFixture.away : null
                  )
                }
              >
                search betsApi
              </Button>
            </SoftBox>
          </div>
        </div>
      </div>
    );
  }

  function platformsCell({ value, ...rest }) {
    return (
      <>
        {rest.row.original.platforms.map((item, index) => {
          let betPlaced = formatDateStringUTCtoLocaleCell({
            value: rest.row.original.betsCreatedAt[index],
            ...rest,
          });
          let settlementDate = formatDateStringUTCtoLocaleCell({
            value: rest.row.original.settlement_date,
            ...rest,
          });
          return (
            <div key={item + "-" + index}>
              {item}
              <HtmlTooltip
                placement="right"
                title={
                  <React.Fragment>
                    <div style={{ display: "flex" }}>
                      <div style={{ width: "fit-content" }}>
                        {"BetId: "}
                        {rest.row.original.betIds[index]}
                      </div>
                      <div style={{ width: "fit-content" }}>
                        {"Placed at: "}
                        {betPlaced}
                      </div>
                      <div style={{ width: "fit-content" }}>
                        {"Settled at: "}
                        {settlementDate}
                      </div>
                    </div>
                  </React.Fragment>
                }
              >
                <Button
                  style={{
                    padding: 0,
                    padding: "0px",
                    width: "30px",
                    maxWidth: "30px",
                    minWidth: "10px",
                  }}
                >
                  <span style={{ paddingLeft: "5px" }}></span>
                  <Icon>info_outlined </Icon>
                </Button>
              </HtmlTooltip>
            </div>
          );
        })}
      </>
    );
  }

  function showUser({ value, ...rest }) {
    return (
      <>
        {value}
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{ display: "flex", flexWrap: "wrap" }}>{rest.row.original.username}</div>
            </React.Fragment>
          }
        >
          <Button
            style={{
              padding: 0,
              padding: "0px",
              width: "30px",
              maxWidth: "30px",
              minWidth: "10px",
            }}
          >
            <span style={{ paddingLeft: "5px" }}> </span>
            <Icon>person_icon </Icon>
          </Button>
        </HtmlTooltip>
      </>
    );
  }

  function showVotes({ value, ...rest }) {
    let listItems = rest.row.original.votes; //.split(",");

    return (
      <>
        {value}
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {!listItems || listItems.length == 0
                  ? "No Votes Exist."
                  : listItems?.map((item, index) => {
                      let provider = availableProviders.find((p) => p.value === item.providerId);
                      let providerName = provider ? provider["info"] : item.providerId;
                      let voteDate = new Date(item.timestamp);
                      return (
                        <div
                          key={index}
                          style={{
                            opacity: item.discarded ? "60%" : "100",
                            color: item.discarded ? "red" : "",
                            width: "max-content",
                            display: "inline-flex",
                          }}
                        >
                          <div className={"provider-" + providerName + "-inverse provider-inverse"}>
                            {providerName}
                          </div>
                          <div>
                            {": "}
                            <span style={{ display: "inline-block", minWidth: "100px" }}>
                              {settlementOutcomes.find((s) => s.value == item.outcomeId).label}
                            </span>
                            {item.originId == 1 ? (
                              <span
                                style={{
                                  color: "#66d432",
                                  display: "inline-block",
                                  minWidth: "150px",
                                }}
                              >
                                Online Settlement
                              </span>
                            ) : item.originId == 2 ? (
                              <span
                                style={{
                                  color: "#d432bb",
                                  display: "inline-block",
                                  minWidth: "150px",
                                }}
                              >
                                Offline Settlement
                              </span>
                            ) : item.originId == 4 ? (
                              <span
                                style={{
                                  color: "#f9d900",
                                  display: "inline-block",
                                  minWidth: "150px",
                                }}
                              >
                                FastTrack
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "#3acaeb",
                                  display: "inline-block",
                                  minWidth: "150px",
                                }}
                              >
                                Operator
                              </span>
                            )}{" "}
                            ({formatDateStringUTCtoLocale(item.timestamp)})
                          </div>
                        </div>
                      );
                    })}
              </div>
            </React.Fragment>
          }
        >
          <Button
            style={{
              padding: 0,
              padding: "0px",
              width: "30px",
              maxWidth: "30px",
              minWidth: "10px",
            }}
          >
            <span style={{ paddingLeft: "5px" }}> </span>
            <Icon>how_to_vote_icon </Icon>
          </Button>
        </HtmlTooltip>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                Re-Calculate Outcome from Votes
              </div>
            </React.Fragment>
          }
        >
          <Button
            style={{
              padding: 0,
              padding: "0px",
              width: "30px",
              maxWidth: "30px",
              minWidth: "10px",
            }}
            onClick={(e) => reEvaluateSettlement(e, rest.row.original)}
          >
            <span style={{ paddingLeft: "5px" }}> </span>
            <Icon>sync_icon</Icon>
          </Button>
        </HtmlTooltip>
      </>
    );
  }

  function confirmationCell({ value, ...rest }) {
    const confirmationCreated = formatDateStringUTCtoLocaleCell({
      value: rest.row.original.settlement_date,
      ...rest,
    });
    return (
      <div style={{ display: "flex" }}>
        <div>
          <div>
            <SoftTypography variant="normal" color="secondary">
              {rest.row.original.confirmed ? "Confirmed" : "Unconfirmed since: "}
            </SoftTypography>
          </div>
          {rest.row.original.confirmed ? "" : confirmationCreated}
          <div
            style={{
              display: rest.row.original.sent ? "none" : "flex",
              width: "130px",
              color: "orange",
            }}
          >
            New Settlements Data Exist. Need to be Checked!
          </div>
        </div>
        <div>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{
              cursor: "pointer",
              lineHeight: 0,
            }}
          >
            <Tooltip title="Delete Confirmation" placement="top">
              <Button
                className={"remove-confirmation-button"}
                onClick={(e) => deleteConfirmations(e, rest.row.original)}
              >
                <Icon>clear_outlined_icon </Icon>
              </Button>
            </Tooltip>
          </SoftTypography>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{
              cursor:
                rest.row.original.confirmed || !rest.row.original.canConfirm ? "normal" : "pointer",
              lineHeight: 0,
              display: !rest.row.original.sent ? "none" : "flex",
            }}
          >
            <Tooltip
              title={
                rest.row.original.canConfirm
                  ? rest.row.original.confirmed
                    ? "Confirmed by Operator"
                    : "Confirm Operator's Outcome"
                  : "Operator and Approver can't be the same person. But only for you..."
              }
              placement="right"
            >
              <Button
                className={
                  rest.row.original.canConfirm
                    ? rest.row.original.confirmed
                      ? "confirmated-button"
                      : "confirmation-button"
                    : "confirmation-button-blocked"
                }
                onClick={(e) => handleConfirmation(e, rest.row.original, "confirm")}
              >
                <Icon
                  className={
                    rest.row.original.confirmed
                      ? "confirm-confirmed"
                      : rest.row.original.canConfirm
                      ? rest.row.original.outcome_id !== rest.row.original.calculated_outcome_id
                        ? "confirm-conflict"
                        : "confirm-passed"
                      : "confirm-blocked"
                  }
                >
                  {rest.row.original.confirmed
                    ? "verified_icon "
                    : rest.row.original.canConfirm
                    ? rest.row.original.outcome_id !== rest.row.original.calculated_outcome_id
                      ? "privacy_tip_icon"
                      : "verified_user_icon"
                    : "admin_panel_settings_icon"}
                </Icon>
              </Button>
            </Tooltip>
          </SoftTypography>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{
              cursor: "pointer",
              lineHeight: 0,
              display: rest.row.original.sent ? "none" : "",
            }}
          >
            <Tooltip
              title={
                rest.row.original.canConfirm
                  ? "Please Re-Confirm"
                  : "Operator and Approver can't be the same person. But only for you..."
              }
              placement="right"
            >
              <Button
                className={
                  rest.row.original.canConfirm
                    ? "confirmation-button"
                    : "confirmation-button-blocked"
                }
                onClick={(e) => handleConfirmation(e, rest.row.original, "reConfirm")}
              >
                <Icon
                  className={
                    rest.row.original.canConfirm
                      ? rest.row.original.outcome_id !== rest.row.original.calculated_outcome_id
                        ? "confirm-conflict"
                        : "confirm-passed"
                      : "confirm-blocked"
                  }
                >
                  {rest.row.original.canConfirm
                    ? rest.row.original.outcome_id !== rest.row.original.calculated_outcome_id
                      ? "privacy_tip_icon"
                      : "verified_user_icon"
                    : "admin_panel_settings_icon"}
                </Icon>
              </Button>
            </Tooltip>
          </SoftTypography>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{
              cursor: "pointer",
              lineHeight: 0,
            }}
          >
            <Button
              className={"radio-confirmation-button"}
              onClick={(e) =>
                handleMultiConfirmations(e, rest.row.original, "multiConfirmationsHandle")
              }
            >
              <Icon
                className={
                  rest.row.original.toConfirm ? "exist-confirmation" : "not-exist-confirmation"
                }
              >
                {!rest.row.original.toConfirm
                  ? "radio_button_unchecked_outlined_icon"
                  : "radio_button_checked_outlined_icon"}
              </Icon>
            </Button>
          </SoftTypography>
        </div>
      </div>
    );
  }

  function formatStatuses({ value, ...rest }) {
    return value ? (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div style={{}}>
              {value.map((item, index) => {
                return (
                  <div
                    key={rest.row.original.fixtureIds[index] + "-" + item}
                    className={item.replace(/ /g, "_").toLowerCase()}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        }
      >
        <div>
          {[...new Set(value)].map((item, index) => {
            return (
              <div
                key={rest.row.original.fixtureIds[index] + "-" + item}
                className={item.replace(/ /g, "_").toLowerCase()}
              >
                {item}
              </div>
            );
          })}
        </div>
      </HtmlTooltip>
    ) : (
      <></>
    );
  }

  function formatDate({ value, ...rest }) {
    let date = new Date(value * 1);

    let dateformatted = formatUTCTostring(value);
    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;
    let alt_start_date_utc_exist =
      rest.row.original.alt_start_date_utc && value !== rest.row.original.alt_start_date_utc
        ? true
        : false;
    return alt_start_date_utc_exist ? (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div style={{}}>
              {rest.row.original.start_dates_utc.map((item, index) => {
                let itemFormatted = formatUTCTostring(item);
                return (
                  <div key={rest.row.original.fixtureIds[index] + "-" + item}>{itemFormatted}</div>
                );
              })}
            </div>
          </React.Fragment>
        }
      >
        <div style={{ color: "orange" }}>{dateformatted}*</div>
      </HtmlTooltip>
    ) : (
      dateformatted
    );
  }

  function formatDateStringUTCtoLocaleCell({ value, ...rest }) {
    if (!value) return "-";
    let date = new Date(value);
    let dateformatted = formatDateStringUTCtoLocale(value);

    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;
    let operator =
      rest.column.id == "settlement_date" ? rest.row.original.settled_by_operator : false;

    let betdate = rest.column.id == "created_at" ? true : false;
    let betMoment = null;
    if (betdate) {
      betMoment = "pre";
      if (date.getTime() > rest.row.original.start_date_utc * 1) betMoment = "live";
    }
    return (
      <div className={betMoment ? betMoment + "-bet" : ""}>
        {dateformatted}
        <Icon style={{ display: operator ? "" : "none", color: "#17c1e8" }}>person_icon</Icon>
      </div>
    );
  }

  function formatDateStringUTCtoLocale(value) {
    if (!value) return "-";
    let date = new Date(value);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
    let secs = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
    let milisecs =
      date.getMilliseconds() < 100
        ? "0" + date.getMilliseconds()
        : date.getMilliseconds() < 10
        ? "00" + date.getMilliseconds()
        : date.getMilliseconds();

    let dateformatted =
      day +
      "/" +
      month +
      "/" +
      date.getFullYear() +
      " " +
      hours +
      ":" +
      minutes +
      ":" +
      secs +
      "." +
      milisecs;
    return dateformatted;
  }

  function formatUuid({ value, ...rest }) {
    return (
      <HtmlTooltip
        placement="top"
        title={
          <React.Fragment>
            <div style={{}}>{value.toString()}</div>
          </React.Fragment>
        }
      >
        <div style={{ cursor: "pointer" }}>
          <span className="info-icon" onClick={(e) => openAdvancedSearch(e, ["odd_uuid", value])}>
            {value.toString().substring(1, 10) + "..."}
          </span>

          <Icon className="info-icon" onClick={(e) => handleUUIDCopy(e, value)}>
            copy
          </Icon>
        </div>
      </HtmlTooltip>
    );
  }

  function formatUTCTostring(value) {
    let date = new Date(value * 1);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

    let dateformatted = day + "/" + month + "/" + date.getFullYear() + " " + hours + ":" + minutes;
    return dateformatted;
  }

  function possibleSettlement({ value, ...rest }) {
    return (
      <SoftSelect
        className="settlement-outcomes"
        style={{ width: "120px !important" }}
        customSmall={true}
        value={settlementOutcomes.find((item) => item.value === value)}
        options={settlementOutcomes}
        onChange={(value) => handleSettlementOutcomeChange(value, rest.row)}
      ></SoftSelect>
    );
  }

  async function fetchSettlementValues() {
    let settlementOutcomesEndPoint = `/api/v1/entities/availableSettlementOutcomes`;
    let response = await getData(settlementOutcomesEndPoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);
      const noneOption = [{ value: 0, label: "Select..." }];
      const apiOptions = response.data.map((item) => {
        return { value: item.id, label: item.description };
      });

      const allOptions = noneOption.concat(apiOptions);

      settlementOutcomes = allOptions.map((item) => {
        return { value: item.value, label: item.label };
      });
    }
  }

  /**
   *
   */
  const handleUUIDCopy = (e, UUID) => {
    //console.log("Copy ApiKey: ", apikey);
    copyToClipboard(UUID);
  };

  const copyToClipboard = (textToCopy) => {
    setApiresponse("success");
    setApiResponseMsg("UUID has been copied to the clipboard!");
    setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  /**
   *
   */
  async function initData() {
    if (!availableProviders || availableProviders.length == 0) return;
    setLoading(true);
    let dataTable = { ...dataTableData };
    setDataTableData({ columns: dataTable.columns, rows: [] });
    const userData = getUserData();
    let userId = userData.id;

    let getTemplatesEndpoint = `/api/v1/bets/settlements/unconfirmed`;

    let response = await postData(getTemplatesEndpoint, userData, {
      Authorization: getAuthorizationHeader(),
    });

    if (response != null) {
      //console.log(response.data);

      if (response.data.length == 0) {
        setApiresponse("error");
        setApiResponseMsg("There are NO Data for the selected filters.");
        setNotification(true);
        setLoading(false);
        return;
      }

      await sleep(1000);
      dataTable.rows = response.data;

      for (let index = 0; index < response.data.length; index++) {
        const row = response.data[index];
        let settlement_date = new Date(row.settlement_date);
        let finished_at_date = row.finished_at_utc ? new Date(row.finished_at_utc * 1) : 0;

        let autoSettlement =
          finished_at_date == 0 ? null : settlement_date > finished_at_date ? "offline" : "online";
        row.votes = row.votes?.sort((a, b) => b.timestamp - a.timestamp);
        row.fixture = row.sport
          .concat(row.region)
          .concat(row.competition)
          .concat(row.home)
          .concat(row.away);

        row.settler = row.settled_by_operator
          ? row.username
            ? "operator: " + row.username
            : "operator"
          : row.settlement_date
          ? "system: " + autoSettlement
          : null;

        row.settlement = settlementOutcomes.find((item) => item.value === row.outcome_id);
        row.settlement = row.settlement ? row.settlement.label : "-";

        row.calculated_settlement = settlementOutcomes.find(
          (item) => item.value === row.calculated_outcome_id
        );
        row.calculated_settlement = row.calculated_settlement
          ? row.calculated_settlement.label
          : "-";

        let start_date_at = new Date(row.start_date_utc * 1);
        let diffTime = null;

        diffTime =
          autoSettlement == "offline"
            ? Math.abs(settlement_date.getTime() - finished_at_date.getTime())
            : Math.abs(settlement_date.getTime() - start_date_at.getTime());

        row.diff = diffTime;
        row.canConfirm = row.settler_id == userId ? false : true;
      }

      setHiddenColumns(["updated", "provider_names", "home", "away"]);
      allDataTableData = { ...dataTable };
      currentDataTableData = { ...dataTable };
      setDataTableData(dataTable);

      setLoading(false);
    } else {
    }
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  async function reEvaluateSettlement(e, data) {
    e.preventDefault();
    const settleOddEndPoint = `/api/v1/bets/settleOdd`;
    const settlementOutcomesVar = {
      uuid: data.uuid,
      outcome_id: data.outcome_id,
      event_id: data.fixture_id ? data.fixture_id : data.event_id,
      provider_id: data.provider_id,
    };
    const response = await postData(
      settleOddEndPoint,
      { signal: "reevaluation", values: [settlementOutcomesVar] },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success").then(
        async (result) => {
          initData();
        }
      );
    } else {
      Swal.fire("Error!", `Something went wrong.`, "error").then(async (result) => {
        initData();
      });
    }
  }

  async function makeConfirmation(data) {
    const settleOddEndPoint = `/api/v1/bets/settleOdd`;
    const settlementOutcomesVar = {
      uuid: data.uuid,
      outcome_id: data.outcome_id,
      event_id: data.fixture_id ? data.fixture_id : data.event_id,
      provider_id: data.provider_id,
      sp: data.sp,
    };
    const response = await postData(
      settleOddEndPoint,
      { signal: "settlement", values: [settlementOutcomesVar] },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      return response;
    } else {
      return null;
    }
  }

  async function confirmSettlement(e, data) {
    e.preventDefault();
    //if (!data.canConfirm || data.confirmed) return;
    if (data.confirmed) return;
    const settleOddEndPoint = `/api/v1/bets/settleOdd`;
    const settlementOutcomesVar = {
      uuid: data.uuid,
      outcome_id: data.outcome_id,
      event_id: data.fixture_id ? data.fixture_id : data.event_id,
      provider_id: data.provider_id,
      sp: data.sp,
    };
    const response = await postData(
      settleOddEndPoint,
      { signal: "settlement", values: [settlementOutcomesVar] },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success").then(
        async (result) => {
          initData();
        }
      );
    } else {
      Swal.fire("Error!", `Something went wrong`, "error").then(async (result) => {
        initData();
      });
    }
  }

  async function reConfirmSettlement(e, data) {
    e.preventDefault();
    const settleOddEndPoint = `/api/v1/bets/settleOdd`;
    const settlementOutcomesVar = {
      uuid: data.uuid,
      outcome_id: data.outcome_id,
      event_id: data.fixture_id ? data.fixture_id : data.event_id,
      provider_id: data.provider_id,
      sp: data.sp,
    };
    const response = await postData(
      settleOddEndPoint,
      { signal: "settlement", values: [settlementOutcomesVar] },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success").then(
        async (result) => {
          initData();
        }
      );
    } else {
      Swal.fire("Error!", `Something went wrong.`, "error").then(async (result) => {
        initData();
      });
    }
  }
  const handleMultiConfirmations = async (e, data, type) => {
    e.preventDefault();
    let currentData = { ...currentDataTableData };
    setDataTableData({ columns: dataTableData.columns, rows: [] });
    setHiddenColumns([]);

    let confirmationItemIndex = confirmationsList.findIndex((c) => c.uuid == data.uuid);
    let itemConfirm = currentData.rows.find((r) => r.uuid == data.uuid);
    let confirmationsListCopy = [...confirmationsList];
    if (confirmationItemIndex > -1) {
      confirmationsListCopy.splice(confirmationItemIndex, 1);
      itemConfirm.toConfirm = false;
    } else {
      confirmationsListCopy.push({ uuid: data.uuid, data: data });
      itemConfirm.toConfirm = true;
    }
    confirmationsList = confirmationsListCopy;
    setDataTableData(currentData);
    setHiddenColumns(["updated", "provider_names", "home", "away"]);

    if (confirmationsListCopy.length > 0) {
      // setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved Confirmations changes.");
      setNotification(true);
      setShowDialog(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("There are NO unsaved market filters changes.");
      setNotification(true);
      setShowDialog(false);
    }
  };

  async function handleConfirmation(e, data, confirm) {
    Swal.fire({
      title: `Unsaved Data!`,
      html: `You are about to confirm a Settlement.
        Do you want to Continue?`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      icon: "warning",
      confirmButtonColor: "#87cb16",
      denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        switch (confirm) {
          case "confirm":
            confirmSettlement(e, data);
            break;
          case "reConfirm":
            reConfirmSettlement(e, data);
            break;
          default:
            break;
        }
      }
    });
  }

  async function deleteConfirmations(e, data) {
    Swal.fire({
      title: `Delete Confirmation?`,
      html: `You are about to Delete this Confirmation request.
        Do you want ro Continue?`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      icon: "warning",
      confirmButtonColor: "#87cb16",
      denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const settleOddEndPoint = `/api/v1/bets/settlements/unconfirmed/${data.uuid}`;

        const response = await delData(
          settleOddEndPoint,
          {
            Authorization: getAuthorizationHeader(),
          },
          deleteConfirmationsErrorHandling
        );

        if (response != null) {
          setApiresponse("success");
          setApiResponseMsg("Confirmation Deleted Succesfull");
          setNotification(true);
          setShowDialog(false);
          initData();
        }
      }
    });
  }

  function deleteConfirmationsErrorHandling(error) {
    setApiresponse("error");
    setApiResponseMsg("Something goes wrong while deleting confirmation. Try again.");
    setNotification(true);
    setShowDialog(true);
    initData();
  }

  function searchGoogle(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("http://google.com/search?q=" + q);
  }

  function searchBetsApi(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("https://betsapi.com/search/?q=" + q.split(" vs ")[0]);
  }

  function searchVbet(e, id) {
    e.preventDefault();
    window.open("https://sport.vbet.am/en/sports/pre-match/results?game=" + id);
  }

  function searchBetfoot(e, id, sport) {
    e.preventDefault();
    if (id && sport) window.open(`https://betfoot.com/live/${sport}/1/${id}`);
  }

  function searchSofa(e, fixture) {
    e.preventDefault();
    let sport = fixture.sport.toLowerCase();
    let home = fixture.home;
    let url = `https://duckduckgo.com/?q=!ducky+inurl:sofascore.com/team/${sport}  ${home}`;
    window.open(url.toString(), "_blank", "noreferrer");
  }

  const preConfirmAll = async () => {
    let datatable = { ...dataTableData };
    confirmationsList = [];
    for (let index = 0; index < datatable.rows.length; index++) {
      const row = datatable.rows[index];
      row.toConfirm = true;
      confirmationsList.push({ uuid: row.uuid, data: row });
    }
    setDataTableData(datatable);

    if (confirmationsList.length > 0) {
      // setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved Confirmations changes.");
      setNotification(true);
      setShowDialog(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("There are NO unsaved Confirmations changes.");
      setNotification(true);
      setShowDialog(false);
    }
  };

  const handleCancelEdit = async () => {
    if (confirmationsList.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html:
          "You are going Lose some Confirmations you have already set.</br></br>" +
          confirmationsList.length +
          " Confirmations have been changed. </br></br> Are you Sure?",
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          confirmationsList = [];
          initData();
          setShowDialog(false);
        }
      });
    } else {
      initData();
      //console.log("Nothing have been changed");
    }
  };

  const handleUpdate = async () => {
    let success = true;
    for (let index = 0; index < confirmationsList.length; index++) {
      const confirmation = confirmationsList[index];
      let response = await makeConfirmation(confirmation.data);
      if (!response) success = false;
    }

    if (success) {
      Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success").then(
        async (result) => {
          confirmationsList = [];
          initData();
          setShowDialog(false);
        }
      );
    } else {
      Swal.fire("Error!", `Something went wrong, with some confirmations.`, "error").then(
        async (result) => {
          confirmationsList = [];
          initData();
          setShowDialog(false);
        }
      );
    }
  };

  useEffect(() => {
    if (providers) {
      //setAvailableProviders(providers);
      availableProviders = providers;
    }
    initData();
  }, [platform.label, sports, providers]);

  useEffect(() => {
    fetchSettlementValues();
  }, []);

  return (
    <DashboardLayout>
      <DialogBox
        // @ts-ignore
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Confirmations List
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the <span style={{ color: "#cb0c9f" }}>{confirmed}</span>{" "}
                Settlements
              </SoftTypography>
            </SoftBox>
            <SoftBox lineHeight={1}>
              <Button
                onClick={handleCancelEdit}
                disabled={confirmationsList.length > 0 ? false : true}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={confirmationsList.length > 0 ? false : true}>
                Confirm
              </Button>
              <Button onClick={preConfirmAll}>Select All</Button>
            </SoftBox>
          </SoftBox>
          <DataTable
            table={dataTableData}
            // bottomPadding={26}
            tableClassName={
              loading
                ? "hide"
                : dataTableData.rows.length > 0
                ? "confirmation-settlement-table"
                : null
            }
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            hiddenColumns={hiddenColumns}
            headerClassName={dataTableData.rows.length > 0 ? "sticky-header" : null}
            autoResetPageDefault={true}
            autoResetGlobalFilter={true}
            autoResetSortBy={true}
            canSearch
            searchValue={externalSearch}
            openBackDrop={loading}
            informParent={informParent}
            refreshEnabled={true}
          ></DataTable>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Confirmation;
